import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MobileNavigation } from '../components/MobileNavigation';
import { Calendar, MapPin, Clock, Users, CreditCard, UsersThree, Wallet, HourglassHigh, FileText, Warning } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';

// Helper to safely extract error message from API responses
const getErrorMessage = (error, defaultMsg = 'An error occurred') => {
  if (!error) return defaultMsg;
  if (typeof error === 'string') return error;
  if (error.detail) {
    if (typeof error.detail === 'string') return error.detail;
    if (Array.isArray(error.detail)) {
      return error.detail.map(e => e.msg || e.message || String(e)).join(', ');
    }
    if (typeof error.detail === 'object') {
      return error.detail.msg || error.detail.message || defaultMsg;
    }
  }
  if (error.message) return error.message;
  return defaultMsg;
};

export function Sessions() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [players, setPlayers] = useState([]);
  const [registering, setRegistering] = useState(null);
  const [joiningWaitlist, setJoiningWaitlist] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [ageGroupFilter, setAgeGroupFilter] = useState('all');
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [playerWaitlistStatus, setPlayerWaitlistStatus] = useState({});
  const [playerWaiverStatus, setPlayerWaiverStatus] = useState({});
  const [playerRegistrations, setPlayerRegistrations] = useState({});
  
  const loadSessions = useCallback(async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/sessions`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      } else {
        toast.error('Failed to load sessions');
      }
    } catch (error) {
      console.error('Load sessions error:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const loadCreditBalance = useCallback(async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/payments/credits/balance`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCreditBalance(data.total_balance || 0);
      }
    } catch (error) {
      console.error('Load credit balance error:', error);
    }
  }, []);
  
  const loadWaitlistStatus = useCallback(async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/parent/waitlist-status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Create a map of player_id -> session_id for quick lookup
        const statusMap = {};
        data.forEach(entry => {
          statusMap[entry.player?.player_id] = {
            session_id: entry.session?.session_id,
            position: entry.position
          };
        });
        setPlayerWaitlistStatus(statusMap);
      }
    } catch (error) {
      console.error('Load waitlist status error:', error);
    }
  }, []);
  
  const loadRegistrations = useCallback(async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/registrations/my`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Create a map of "player_id-session_id" -> registration for quick lookup
        const regMap = {};
        data.forEach(reg => {
          if (reg.status === 'registered') {
            const key = `${reg.player_id}-${reg.session_id}`;
            regMap[key] = reg;
          }
        });
        setPlayerRegistrations(regMap);
      }
    } catch (error) {
      console.error('Load registrations error:', error);
    }
  }, []);
  
  const loadWaiverStatus = useCallback(async (playerList) => {
    // Load waiver status for all players
    const statusMap = {};
    for (const player of playerList) {
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/waivers/player/${player.player_id}/status`, {
          credentials: 'include'
        });
        if (response.ok) {
          const status = await response.json();
          statusMap[player.player_id] = {
            signed: status.signed && !status.expired,
            expired: status.expired
          };
        } else {
          statusMap[player.player_id] = { signed: false, expired: false };
        }
      } catch (error) {
        statusMap[player.player_id] = { signed: false, expired: false };
      }
    }
    setPlayerWaiverStatus(statusMap);
  }, []);
  
  useEffect(() => {
    loadSessions();
    loadPlayers();
    loadCreditBalance();
    loadWaitlistStatus();
    loadRegistrations();
    
    // Reload data when page becomes visible (after returning from payment)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadSessions();
        loadCreditBalance();
        loadWaitlistStatus();
        loadRegistrations();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadSessions, loadCreditBalance, loadWaitlistStatus, loadRegistrations]);
  
  // Reload sessions when navigating back to this page
  useEffect(() => {
    loadSessions();
    loadCreditBalance();
    loadWaitlistStatus();
    loadRegistrations();
  }, [location.key, loadSessions, loadCreditBalance, loadWaitlistStatus, loadRegistrations]);
  
  // Filter sessions by grade group
  const filteredSessions = ageGroupFilter === 'all' 
    ? sessions 
    : sessions.filter(s => (s.grade_group || s.age_group) === ageGroupFilter);
  
  const loadPlayers = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/players`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
        // Load waiver status for all players
        loadWaiverStatus(data);
      }
    } catch (error) {
      console.error('Load players error:', error);
    }
  };
  
  const handleRegistration = async (sessionId, playerId) => {
    // First check waiver status
    try {
      const waiverResponse = await fetch(`${getApiBaseUrl()}/api/waivers/player/${playerId}/status`, {
        credentials: 'include'
      });
      
      if (waiverResponse.ok) {
        const waiverStatus = await waiverResponse.json();
        if (!waiverStatus.signed || waiverStatus.expired) {
          toast.error('Please sign the waiver before registering. Go to Waivers page to sign.');
          return;
        }
      } else {
        toast.error('Please sign the waiver before registering. Go to Waivers page to sign.');
        return;
      }
    } catch (error) {
      console.error('Waiver check error:', error);
      toast.error('Please sign the waiver before registering. Go to Waivers page to sign.');
      return;
    }
    
    // Then get pricing information
    try {
      const pricingResponse = await fetch(`${getApiBaseUrl()}/api/pricing/session/${sessionId}`, {
        credentials: 'include'
      });
      
      if (pricingResponse.ok) {
        const pricingData = await pricingResponse.json();
        
        if (pricingData.pricing.base_price > 0) {
          // Show pricing dialog for paid sessions
          setSelectedSession(sessionId);
          setSelectedPlayer(playerId);
          setPricing(pricingData.pricing);
          setShowPaymentDialog(true);
          return;
        }
      }
    } catch (error) {
      console.error('Pricing fetch error:', error);
    }
    
    // Proceed with free registration
    proceedWithRegistration(sessionId, playerId);
  };
  
  const proceedWithRegistration = async (sessionId, playerId) => {
    setRegistering(`${sessionId}-${playerId}`);
    
    try {
      // Check waiver status first
      const waiverResponse = await fetch(`${getApiBaseUrl()}/api/waivers/player/${playerId}/status`, {
        credentials: 'include'
      });
      
      if (waiverResponse.ok) {
        const waiverStatus = await waiverResponse.json();
        if (!waiverStatus.signed || waiverStatus.expired) {
          toast.error('Valid waiver required before registration');
          setRegistering(null);
          return;
        }
      }
      
      // Register for session
      const response = await fetch(`${getApiBaseUrl()}/api/registrations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          player_id: playerId
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.payment_required) {
          // Redirect to payment
          const paymentResponse = await fetch(`${getApiBaseUrl()}/api/payments/checkout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
              session_id: sessionId,
              player_id: playerId,
              origin_url: window.location.origin
            })
          });
          
          if (paymentResponse.ok) {
            const paymentData = await paymentResponse.json();
            window.location.href = paymentData.url;
          } else {
            toast.error('Payment setup failed');
          }
        } else {
          toast.success(`Successfully registered! Status: ${result.status}`);
          loadSessions();
        }
      } else {
        const error = await response.json();
        toast.error(getErrorMessage(error, 'Registration failed'));
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed');
    } finally {
      setRegistering(null);
    }
  };
  
  const handleJoinWaitlist = async (sessionId, playerId) => {
    setJoiningWaitlist(`${sessionId}-${playerId}`);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/sessions/${sessionId}/join-waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ player_id: playerId })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        loadWaitlistStatus();
      } else {
        const error = await response.json();
        toast.error(getErrorMessage(error, 'Failed to join waitlist'));
      }
    } catch (error) {
      console.error('Join waitlist error:', error);
      toast.error('Failed to join waitlist');
    } finally {
      setJoiningWaitlist(null);
    }
  };
  
  const handleLeaveWaitlist = async (sessionId, playerId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/sessions/${sessionId}/leave-waitlist/${playerId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Removed from waitlist');
        loadWaitlistStatus();
      } else {
        const error = await response.json();
        toast.error(getErrorMessage(error, 'Failed to leave waitlist'));
      }
    } catch (error) {
      console.error('Leave waitlist error:', error);
      toast.error('Failed to leave waitlist');
    }
  };
  
  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return;
    
    setValidatingDiscount(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/discount-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code: discountCode,
          amount: pricing?.total || 17.08
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setDiscountApplied(result);
        toast.success(`Discount applied: -$${result.discount_amount.toFixed(2)}`);
      } else {
        const error = await response.json();
        toast.error(getErrorMessage(error, 'Invalid discount code'));
        setDiscountApplied(null);
      }
    } catch (error) {
      console.error('Validate discount error:', error);
      toast.error('Failed to validate discount code');
    } finally {
      setValidatingDiscount(false);
    }
  };
  
  const removeDiscount = () => {
    setDiscountCode('');
    setDiscountApplied(null);
  };
  
  const handleStripePayment = async () => {
    // Validate required fields
    if (!selectedSession || !selectedPlayer) {
      toast.error('Please select a session and player first');
      return;
    }
    
    setProcessingPayment(true);
    try {
      console.log('Starting Stripe checkout...', { selectedSession, selectedPlayer });
      const response = await fetch(`${getApiBaseUrl()}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: selectedSession,
          player_id: selectedPlayer,
          origin_url: window.location.origin,
          discount_code: discountApplied?.code || null
        })
      });
      
      console.log('Checkout response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Checkout response:', data);
        
        // Check if payment was completed with credit only (no Stripe redirect needed)
        if (data.payment_method === 'credit_only') {
          toast.success(`Registration complete! ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(data.credit_applied)} credit applied.`);
          setShowPaymentDialog(false);
          setProcessingPayment(false);
          loadSessions();
          loadPlayers();
          loadCreditBalance();
          return;
        }
        
        // Check if this was a free session
        if (data.payment_method === 'free') {
          toast.success('Free registration complete!');
          setShowPaymentDialog(false);
          setProcessingPayment(false);
          loadSessions();
          loadPlayers();
          return;
        }
        
        // Validate we have a URL before redirecting
        if (data.url && typeof data.url === 'string' && data.url.startsWith('http')) {
          console.log('Redirecting to Stripe:', data.url.substring(0, 50));
          window.location.href = data.url;
        } else {
          console.error('Invalid checkout URL:', data);
          toast.error('Payment setup failed - invalid response');
          setProcessingPayment(false);
        }
      } else {
        const error = await response.json();
        console.error('Checkout error:', error);
        toast.error(getErrorMessage(error, 'Payment setup failed'));
        setProcessingPayment(false);
      }
    } catch (error) {
      console.error('Stripe payment error:', error);
      toast.error('Payment failed: ' + (error.message || 'Unknown error'));
      setProcessingPayment(false);
    }
  };
  
  const handlePayPalPayment = async () => {
    // Validate required fields
    if (!selectedSession || !selectedPlayer) {
      toast.error('Please select a session and player first');
      return;
    }
    
    setProcessingPayment(true);
    try {
      console.log('Starting PayPal checkout...', { selectedSession, selectedPlayer });
      const response = await fetch(`${getApiBaseUrl()}/api/payments/paypal/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: selectedSession,
          player_id: selectedPlayer,
          origin_url: window.location.origin,
          discount_code: discountApplied?.code || null
        })
      });
      
      console.log('PayPal response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('PayPal response:', data);
        
        if (data.approve_url && typeof data.approve_url === 'string' && data.approve_url.startsWith('http')) {
          console.log('Redirecting to PayPal:', data.approve_url.substring(0, 50));
          window.location.href = data.approve_url;
        } else {
          console.error('Invalid PayPal URL:', data);
          toast.error('PayPal setup failed - invalid response');
          setProcessingPayment(false);
        }
      } else {
        const error = await response.json();
        console.error('PayPal error:', error);
        toast.error(getErrorMessage(error, 'PayPal payment failed'));
        setProcessingPayment(false);
      }
    } catch (error) {
      console.error('PayPal payment error:', error);
      toast.error('Payment failed: ' + (error.message || 'Unknown error'));
      setProcessingPayment(false);
    }
  };
  
  const formatDate = (dateString) => {
    // Parse as local date to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const time = new Date();
    time.setHours(parseInt(hours), parseInt(minutes));
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  const getAvailabilityStatus = (session) => {
    const available = session.capacity - session.registered_count;
    if (available > 5) return { color: 'bg-green-500', text: `${available} spots left` };
    if (available > 0) return { color: 'bg-yellow-500', text: `${available} spots left` };
    return { color: 'bg-red-500', text: 'Full - Waitlist available' };
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-24" data-testid="sessions-page">
      {/* Full-screen loading overlay when processing payment */}
      {processingPayment && (
        <div className="fixed inset-0 bg-background/95 z-[100] flex flex-col items-center justify-center">
          <LoadingSpinner size="xl" />
          <p className="mt-4 text-lg font-medium">Connecting to payment...</p>
          <p className="text-sm text-muted-foreground mt-2">Please wait, do not close this page</p>
        </div>
      )}
      
      {/* Premium Header */}
      <div className="glass-effect sticky top-0 z-40">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Book Your Spot</p>
              <h1 className="text-2xl font-black uppercase tracking-tight">
                {filteredSessions.length} Session{filteredSessions.length !== 1 ? 's' : ''}
              </h1>
            </div>
            
            {/* Grade Filter */}
            <Select value={ageGroupFilter} onValueChange={setAgeGroupFilter}>
              <SelectTrigger className="w-44 bg-card border-border" data-testid="grade-filter">
                <SelectValue placeholder="Grade Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Grades</SelectItem>
                <SelectItem value="elementary">Elementary (3-5)</SelectItem>
                <SelectItem value="middle">Middle School (6-8)</SelectItem>
                <SelectItem value="high">High School (9-12)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Credit Balance Banner */}
          {creditBalance > 0 && (
            <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
              <Wallet size={20} className="text-green-500" />
              <span className="text-sm text-green-500 font-medium">
                ${creditBalance.toFixed(2)} account credit available - auto-applied at checkout
              </span>
            </div>
          )}
        </div>
      </div>
      
      {/* Sessions List */}
      <div className="px-6 py-8">
        {filteredSessions.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12">
              <Calendar size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-lg mb-2">NO SESSIONS AVAILABLE</h3>
              <p className="text-muted-foreground">
                {ageGroupFilter !== 'all' 
                  ? `No sessions available for this grade level. Try a different selection.`
                  : 'Check back later for new basketball sessions.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6 stagger-children">
            {filteredSessions.map((session) => {
              const availability = getAvailabilityStatus(session);
              const gradeDisplay = session.grade_group === 'elementary' ? '3rd-5th Grade' :
                                   session.grade_group === 'middle' ? '6th-8th Grade' :
                                   session.grade_group === 'high' ? '9th-12th Grade' :
                                   session.age_group || 'Elementary';
              const spotsLeft = session.capacity - session.registered_count;
              const isFree = !session.price || session.price === 0;
              
              // Check if session has ended (date is in the past)
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const sessionDate = new Date(session.date + 'T00:00:00');
              const isSessionEnded = sessionDate < today || session.status === 'ended';
              
              return (
                <Card key={session.session_id} className="bg-card border-border card-hover overflow-hidden">
                  {/* Session Header with Date Banner */}
                  <div className="bg-gradient-to-r from-primary/20 via-primary/10 to-transparent p-4 border-b border-border/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 bg-card rounded-xl flex flex-col items-center justify-center border border-border">
                          <span className="text-xs uppercase text-muted-foreground font-bold">
                            {new Date(session.date.split('-')[0], session.date.split('-')[1] - 1, session.date.split('-')[2]).toLocaleDateString('en-US', { weekday: 'short' })}
                          </span>
                          <span className="text-xl font-black text-primary">
                            {session.date.split('-')[2]}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-heading text-xl">{formatDate(session.date)}</h3>
                          <p className="text-sm text-muted-foreground">
                            {formatTime(session.start_time)} - {formatTime(session.end_time)}
                          </p>
                        </div>
                      </div>
                      <Badge className={`${isSessionEnded ? 'bg-gray-500' : availability.color} text-white px-3 py-1`}>
                        {isSessionEnded ? 'Session Ended' : availability.text}
                      </Badge>
                    </div>
                  </div>
                  
                  <CardContent className="p-5">
                    <div className="space-y-4">
                      {/* Session Details Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                            <UsersThree size={20} weight="duotone" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{gradeDisplay}</p>
                            <p className="text-xs text-muted-foreground">Grade Level</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0">
                            <MapPin size={20} weight="duotone" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{session.location?.name}</p>
                            <p className="text-xs text-muted-foreground">{session.court || 'Main Court'}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                            spotsLeft <= 3 ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-500'
                          }`}>
                            <Users size={20} weight="duotone" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{spotsLeft} spots left</p>
                            <p className="text-xs text-muted-foreground">{session.registered_count} / {session.capacity} registered</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                            isFree ? 'bg-green-500/10 text-green-500' : 'bg-primary/10 text-primary'
                          }`}>
                            <CreditCard size={20} weight="duotone" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">
                              {isFree ? 'FREE' : `$${session.price.toFixed(2)}`}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {isFree ? 'No charge' : '+ taxes & fees'}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Session Description (if available) */}
                      {session.description && (
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <p className="text-sm text-muted-foreground">{session.description}</p>
                        </div>
                      )}
                      
                      {/* Player Registration */}
                      {isSessionEnded ? (
                        <div className="text-center py-6 border-t border-border">
                          <Clock size={32} weight="duotone" className="mx-auto text-gray-500 mb-2" />
                          <p className="text-gray-500 font-medium">This session has ended</p>
                          <p className="text-xs text-muted-foreground mt-1">Registration is closed for past sessions</p>
                        </div>
                      ) : players.length === 0 ? (
                        <div className="text-center py-6 border-t border-border">
                          <Users size={32} weight="duotone" className="mx-auto text-muted-foreground mb-2" />
                          <p className="text-muted-foreground mb-3">Add a player to register</p>
                          <Button 
                            onClick={() => window.location.href = '/players'}
                            className="btn-primary"
                          >
                            Add Player
                          </Button>
                        </div>
                      ) : (
                        <div className="border-t border-border pt-4">
                          <p className="text-sm font-heading uppercase tracking-wide mb-3">
                            {session.registered_count >= session.capacity 
                              ? 'Join Waitlist' 
                              : 'Register Player'}
                          </p>
                          <div className="space-y-2">
                            {players.map((player) => {
                              const isFull = session.registered_count >= session.capacity;
                              const waitlistInfo = playerWaitlistStatus[player.player_id];
                              const isOnThisWaitlist = waitlistInfo?.session_id === session.session_id;
                              const isOnAnotherWaitlist = waitlistInfo && !isOnThisWaitlist;
                              const waiverStatus = playerWaiverStatus[player.player_id];
                              const hasSignedWaiver = waiverStatus?.signed;
                              
                              // Check if player is already registered for this session
                              const regKey = `${player.player_id}-${session.session_id}`;
                              const isAlreadyRegistered = !!playerRegistrations[regKey];
                              
                              return (
                                <div key={player.player_id} className={`flex items-center justify-between p-3 rounded-lg ${
                                  isAlreadyRegistered ? 'bg-green-500/10 border border-green-500/30' : 'bg-muted/20'
                                }`}>
                                  <div>
                                    <p className="font-medium text-sm">{player.name}</p>
                                    <p className="text-xs text-muted-foreground">Age: {player.age}</p>
                                    {isAlreadyRegistered && (
                                      <p className="text-xs text-green-500 font-medium mt-1 flex items-center gap-1">
                                        <Users size={12} />
                                        Already registered
                                      </p>
                                    )}
                                    {!isAlreadyRegistered && !hasSignedWaiver && (
                                      <p className="text-xs text-yellow-500 font-medium mt-1 flex items-center gap-1">
                                        <Warning size={12} />
                                        Waiver required
                                      </p>
                                    )}
                                    {!isAlreadyRegistered && isOnThisWaitlist && (
                                      <p className="text-xs text-yellow-500 font-medium mt-1">
                                        <HourglassHigh size={12} className="inline mr-1" />
                                        Waitlist position #{waitlistInfo.position}
                                      </p>
                                    )}
                                    {!isAlreadyRegistered && isOnAnotherWaitlist && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Already on another waitlist
                                      </p>
                                    )}
                                  </div>
                                  
                                  {isAlreadyRegistered ? (
                                    <span className="text-xs px-3 py-1.5 rounded-full bg-green-500/20 text-green-500 font-medium flex items-center gap-1">
                                      <Users size={14} />
                                      Registered
                                    </span>
                                  ) : !hasSignedWaiver ? (
                                    <Button
                                      size="sm"
                                      onClick={() => window.location.href = '/waivers'}
                                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                                      data-testid={`sign-waiver-${player.player_id}`}
                                    >
                                      <FileText size={14} className="mr-1" />
                                      Sign Waiver
                                    </Button>
                                  ) : isOnThisWaitlist ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleLeaveWaitlist(session.session_id, player.player_id)}
                                      className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                                      data-testid={`leave-waitlist-${player.player_id}-${session.session_id}`}
                                    >
                                      Leave Waitlist
                                    </Button>
                                  ) : isFull ? (
                                    <Button
                                      size="sm"
                                      onClick={() => handleJoinWaitlist(session.session_id, player.player_id)}
                                      disabled={joiningWaitlist === `${session.session_id}-${player.player_id}` || isOnAnotherWaitlist}
                                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                                      data-testid={`join-waitlist-${player.player_id}-${session.session_id}`}
                                    >
                                      {joiningWaitlist === `${session.session_id}-${player.player_id}` ? (
                                        <LoadingSpinner size="sm" />
                                      ) : (
                                        <>
                                          <HourglassHigh size={14} className="mr-1" />
                                          Join Waitlist
                                        </>
                                      )}
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      onClick={() => handleRegistration(session.session_id, player.player_id)}
                                      disabled={registering === `${session.session_id}-${player.player_id}`}
                                      className="btn-primary"
                                      data-testid={`register-${player.player_id}-${session.session_id}`}
                                    >
                                      {registering === `${session.session_id}-${player.player_id}` ? (
                                        <LoadingSpinner size="sm" />
                                      ) : (
                                        'Register'
                                      )}
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Payment Method Selection Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-center">CHOOSE PAYMENT METHOD</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {pricing && (
              <>
                <div className="text-center mb-4">
                  <p className="text-muted-foreground text-sm">
                    Session registration fee breakdown:
                  </p>
                </div>
                
                <div className="bg-muted/20 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Session Fee</span>
                    <span className="font-medium">${pricing.base_price?.toFixed(2) || '15.00'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Tax (8.75%)</span>
                    <span className="font-medium">${pricing.tax_amount?.toFixed(2) || '1.31'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Processing Fee</span>
                    <span className="font-medium">${pricing.processing_fee?.toFixed(2) || '0.77'}</span>
                  </div>
                  
                  {discountApplied && (
                    <div className="flex justify-between items-center text-green-500">
                      <span className="text-sm">Discount ({discountApplied.code})</span>
                      <span className="font-medium">-${discountApplied.discount_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {/* Account Credit Display */}
                  {creditBalance > 0 && (
                    <div className="flex justify-between items-center text-blue-500">
                      <span className="text-sm flex items-center gap-1">
                        <Wallet size={14} /> Account Credit
                      </span>
                      <span className="font-medium">
                        -${Math.min(creditBalance, discountApplied ? discountApplied.final_amount : pricing.total).toFixed(2)} available
                      </span>
                    </div>
                  )}
                  
                  <div className="border-t border-border pt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-bold">Total</span>
                      <span className="font-bold text-primary text-lg">
                        ${discountApplied ? discountApplied.final_amount?.toFixed(2) : (pricing.total?.toFixed(2) || '17.08')}
                      </span>
                    </div>
                    {creditBalance > 0 && (
                      <p className="text-xs text-blue-500 mt-1">
                        {creditBalance >= (discountApplied ? discountApplied.final_amount : pricing.total) 
                          ? "✓ Fully covered by account credit - no card required!"
                          : `Account credit will be applied first (${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(creditBalance)} available)`
                        }
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Discount Code Input */}
                <div className="space-y-2">
                  <p className="text-sm font-medium">Have a discount code?</p>
                  {discountApplied ? (
                    <div className="flex items-center justify-between p-2 bg-green-500/10 rounded-lg border border-green-500/30">
                      <div className="flex items-center gap-2">
                        <span className="text-green-500 font-mono font-bold">{discountApplied.code}</span>
                        <span className="text-sm text-green-500">
                          ({discountApplied.discount_type === 'percentage' 
                            ? `${discountApplied.discount_value}% off` 
                            : `$${discountApplied.discount_value} off`})
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={removeDiscount}
                        className="text-red-500 hover:text-red-600 h-8"
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="uppercase flex-1"
                        onKeyPress={(e) => e.key === 'Enter' && validateDiscountCode()}
                        data-testid="discount-code-input"
                      />
                      <Button
                        onClick={validateDiscountCode}
                        disabled={validatingDiscount || !discountCode.trim()}
                        variant="outline"
                      >
                        {validatingDiscount ? <LoadingSpinner size="sm" /> : 'Apply'}
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="space-y-3 pt-2">
                  <p className="text-sm font-medium text-center">Select payment method:</p>
                  
                  {/* Stripe Option */}
                  <Button
                    onClick={handleStripePayment}
                    disabled={processingPayment}
                    className="w-full h-14 bg-[#635BFF] hover:bg-[#5851DB] text-white"
                    data-testid="stripe-payment-btn"
                  >
                    {processingPayment ? (
                      <LoadingSpinner size="sm" className="mr-2" />
                    ) : (
                      <CreditCard size={20} className="mr-2" weight="bold" />
                    )}
                    Pay with Card (Stripe)
                  </Button>
                  
                  {/* PayPal Option */}
                  <Button
                    onClick={handlePayPalPayment}
                    disabled={processingPayment}
                    className="w-full h-14 bg-[#0070BA] hover:bg-[#003087] text-white"
                    data-testid="paypal-payment-btn"
                  >
                    {processingPayment ? (
                      <LoadingSpinner size="sm" className="mr-2" />
                    ) : (
                      <Wallet size={20} className="mr-2" weight="bold" />
                    )}
                    Pay with PayPal
                  </Button>
                </div>
                
                <div className="text-xs text-muted-foreground text-center space-y-1 pt-2">
                  <p>• Secure payment processing</p>
                  <p>• Registration confirmed after successful payment</p>
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => setShowPaymentDialog(false)}
                  className="w-full"
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      <MobileNavigation userRole="parent" />
    </div>
  );
}